import { useCallback, useEffect, useState } from 'react';
import { trainingService, TrainingNavItem } from '../services/trainingService';
import { getApiErrorMessage } from '../lib/apiError';

/**
 * Everything the training manual drawer does to the server, in one place, so
 * the drawer stays presentational.
 *
 * Navigation and document content load separately and fail separately: a
 * contents list that will not load is a different problem from one document
 * that will not open, and the drawer shows them in different places.
 */
export function useTrainingManual(open: boolean) {
  const [navigation, setNavigation] = useState<TrainingNavItem[]>([]);
  const [activeDocId, setActiveDocId] = useState<string>('');
  const [content, setContent] = useState<string>('');
  const [title, setTitle] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [navLoading, setNavLoading] = useState<boolean>(true);
  const [navError, setNavError] = useState<string | null>(null);

  const loadDocument = useCallback(async (docId: string) => {
    try {
      setLoading(true);
      setError(null);
      setActiveDocId(docId);

      const data = await trainingService.getDocContent(docId);
      setContent(data.content);
      setTitle(data.title);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load this document'));
    } finally {
      setLoading(false);
    }
  }, []);

  // Load once, not on every open. Reopening the drawer keeps whichever
  // document you were reading instead of bouncing back to the default, and
  // costs no requests. A failed load leaves navigation empty, so the next open
  // retries.
  useEffect(() => {
    if (!open || navigation.length > 0) return;

    const fetchNavigation = async () => {
      try {
        setNavLoading(true);
        setNavError(null);
        const data = await trainingService.getNavigation();
        setNavigation(data.navigation);

        if (data.defaultDocId) {
          loadDocument(data.defaultDocId);
        }
      } catch (err) {
        setNavError(getApiErrorMessage(err, 'Failed to load the training manual'));
      } finally {
        setNavLoading(false);
      }
    };

    fetchNavigation();
  }, [open, navigation.length, loadDocument]);

  /** Ignore a click on the document already open, so it does not refetch. */
  const selectDoc = useCallback(
    (docId: string) => {
      if (docId !== activeDocId) {
        loadDocument(docId);
      }
    },
    [activeDocId, loadDocument]
  );

  /** Mobile only: drop back from the reading pane to the contents list. */
  const clearActiveDoc = useCallback(() => setActiveDocId(''), []);

  return {
    navigation,
    activeDocId,
    content,
    title,
    loading,
    error,
    navLoading,
    navError,
    selectDoc,
    loadDocument,
    clearActiveDoc,
  };
}
